import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import type { UserNode } from '/@/hooks/nodes';
import { useCreateUserNode, useOwnUserNode, useUpdateUserNode } from '/@/hooks/nodes';
import { useIsValidatedAdvisor, useIsValidatedConsultant } from '/@/hooks/session';
import { formatPhoneNumber } from '/@/utils/phone-numbers';
import type { GrowerFormValues } from './grower-form';

export type GrowerFormSubmitResult = { success: boolean; growerId?: number };

type UseGrowerFormOptions = {
    identifierDisplay: string;
    mode?: 'create' | 'edit';
    editNodeId?: number;
    initialValues?: Partial<GrowerFormValues>;
    onSubmit?: (data: GrowerFormValues, createdNode?: UserNode) => void | Promise<void>;
    /** When true (email-identifier tenants), omit CUIT on create. */
    omitCuit?: boolean;
};

export function useGrowerForm({
    identifierDisplay,
    mode = 'create',
    editNodeId,
    initialValues,
    onSubmit: onSubmitProp,
    omitCuit = false,
}: UseGrowerFormOptions) {
    const isConsultant = useIsValidatedConsultant();
    const isAdvisor = useIsValidatedAdvisor();

    const form = useForm<GrowerFormValues>({
        mode: 'onChange',
        defaultValues: {
            advisor: initialValues?.advisor ?? '',
            rv: initialValues?.rv ?? '',
            segment: initialValues?.segment ?? '',
            name: initialValues?.name ?? '',
            location: initialValues?.location ?? '',
            email: initialValues?.email ?? '',
            phone: initialValues?.phone ?? '',
        },
    });

    useEffect(() => {
        if (mode === 'edit' && initialValues) {
            form.reset(initialValues);
        }
    }, [mode, initialValues, form.reset]);

    const createUserNodeMutation = useCreateUserNode();
    const updateUserNodeMutation = useUpdateUserNode();
    const ownUserNodeQuery = useOwnUserNode();

    const saveGrower = useCallback(
        async (data: GrowerFormValues): Promise<UserNode | undefined> => {
            const phoneValue = data.phone && data.phone.trim() !== '' ? formatPhoneNumber(data.phone) : undefined;
            if (mode === 'edit' && editNodeId !== undefined) {
                await updateUserNodeMutation.trigger({
                    id: editNodeId,
                    data: {
                        unassignedUserData: {
                            name: data.name,
                            email: data.email,
                            phone: phoneValue,
                            location: data.location,
                        },
                    },
                });
                return undefined;
            }
            let parentIds: number[];
            if (isConsultant) {
                parentIds = data.rv ? [Number(data.rv)] : [];
            } else {
                parentIds = [ownUserNodeQuery.data?.id].filter((id): id is number => id != null);
                if (data.advisor) {
                    const advisorId = Number(data.advisor);
                    if (!Number.isNaN(advisorId)) parentIds.push(advisorId);
                }
            }
            return createUserNodeMutation.trigger({
                type: 'grower',
                unassignedUserData: {
                    name: data.name,
                    email: data.email,
                    phone: phoneValue,
                    location: data.location,
                },
                parents: parentIds.length ? parentIds : undefined,
                subtype: data.segment === 'premium' ? 'premium' : 'common',
                cuit: omitCuit ? undefined : identifierDisplay.replace(/\D/g, '').slice(0, 11) || undefined,
            });
        },
        [
            mode,
            editNodeId,
            isConsultant,
            ownUserNodeQuery.data?.id,
            identifierDisplay,
            omitCuit,
            createUserNodeMutation,
            updateUserNodeMutation,
        ]
    );

    const submitGrower = useCallback(
        () =>
            new Promise<GrowerFormSubmitResult>((resolve) => {
                form.handleSubmit(
                    async (data) => {
                        try {
                            const createdNode = await saveGrower(data);
                            if (mode === 'edit' || createdNode) {
                                await onSubmitProp?.(data, createdNode);
                                resolve({ success: true, growerId: createdNode?.id });
                                return;
                            }
                            resolve({ success: false });
                        } catch {
                            resolve({ success: false });
                        }
                    },
                    () => resolve({ success: false })
                )();
            }),
        [form, saveGrower, onSubmitProp, mode]
    );

    const onSubmit = form.handleSubmit(async (data) => {
        const createdNode = await saveGrower(data);
        if (mode === 'edit' || createdNode) {
            await onSubmitProp?.(data, createdNode);
        }
    });

    const watched = form.watch();
    const requiredFilled =
        !!watched.name?.trim() &&
        !!watched.location?.trim() &&
        !!watched.email?.trim() &&
        ((isAdvisor && (watched.segment === 'premium' || watched.segment === 'common')) || !isAdvisor) &&
        (!isConsultant || !!watched.rv);

    useEffect(() => {
        if (requiredFilled && mode !== 'edit') {
            form.trigger();
        }
    }, [requiredFilled, mode, form.trigger]);

    const hasErrors = Object.keys(form.formState.errors).length > 0;
    const isValid = requiredFilled && !hasErrors;
    const isSubmitting = createUserNodeMutation.isMutating || updateUserNodeMutation.isMutating;

    return {
        form,
        onSubmit,
        submitGrower,
        isValid,
        isSubmitting,
        ownUserNodeQuery,
    };
}
